import React from "react";
import { css } from "@linaria/core";
import { color } from "@ui";
import { colors, spacings } from "@ui/Theme";
import useStyleTag from "@ui/lib/hooks/useStyleTag";
import validateInterface from "@ui/lib/validateInterface";
import { QUANTITY_GAP_SERIE_ID } from "../getQuantityGapsSerie";
import ConfidenceIndicator from "@ui/Indicator/Confidence";
import t from "../../../../../i18n";
import Text from "@ui/Text";
import formatNumber from "@ui/i18n/formatNumber";
import { renderToString } from "react-dom/server";
import cx from "classnames";
import { FailureImpactMetadata } from "../../../Chart/PointMetadata";
import type { Quantity } from "../../../../../api/useQuantities";
import { StarFill } from "@ui/IconV2/react";
import { QUANTITY_UNEXPLAINED_GAP_ID } from "../../../Chart/series/serieIds";

export function useConstantTooltipFormatter(
  quantityByName: {
    [quantityName: string]: Quantity;
  },
  skipZeroImpact = false,
) {
  const classListRef = React.useRef(new Set());
  const { appendCSS } = useStyleTag();

  const quantityByNameRef = React.useRef(quantityByName);
  quantityByNameRef.current = quantityByName;

  return React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const quantity = quantityByNameRef.current[this.x];
      if (!quantity) throw new Error("Value should match a quantity's technicalName");
      if (!this.points) return;

      const faults: React.ReactNode[] = [];
      for (const point of this.points) {
        const serieId = point.series.options.id;
        if (!serieId) throw new Error("Missing options.id");

        const metadata = validateInterface(FailureImpactMetadata, point.point.options.custom);

        /** @note Skip if impact is zero and skipZeroImpact is true */
        const zeroImpact =
          typeof metadata.human === "string"
            ? Math.abs(Number(metadata.human)) < 0.01
            : Math.abs(metadata.human.value) < 0.01;
        if (skipZeroImpact && zeroImpact) continue;

        const isPromoted = metadata.isPromoted === true;
        const fillColor = point.series.options.color as string;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const borderColor = (point.series.options as any).borderColor;
        const className = `DiagnosisDetails-PointFormatter-${serieId.replaceAll(/\W/g, "-")}-${fillColor.substring(1)}`;

        const faultType =
          point.point.series.userOptions.id === QUANTITY_GAP_SERIE_ID ||
          point.point.series.userOptions.id === QUANTITY_UNEXPLAINED_GAP_ID;

        const label = faultType
          ? t("project.diagnosis.symptom.histogram.tooltip.fault_type.gap")
          : t("project.diagnosis.symptom.histogram.tooltip.fault_type.impact");

        const value =
          typeof metadata.human === "string"
            ? metadata.human
            : formatNumber(metadata.human.value, {
                unit: metadata.human.unit ?? quantity["unit"] ?? "",
                precision: 2,
                sign: true,
              });

        const serieName =
          "failureDisplayName" in point.point.custom
            ? point.point.custom.failureDisplayName
            : point.series.name;

        if (!classListRef.current.has(className)) {
          classListRef.current.add(className);
          appendCSS(
            `.${className} { background-color: ${fillColor}; border: 2px solid ${borderColor}; }`,
          );
        }

        faults.push(
          <div className={faultSerie}>
            <div className={cx(square, className)} />
            <div className={faultSerieInfo}>
              <Text type="body2SemiBold" className={faultSerieName}>
                {serieName}
              </Text>
              <div className={faultSerieIcons}>
                {isPromoted && (
                  <StarFill size="xs" color={colors.raw.phosphorus400} className={starIcon} />
                )}
                {typeof metadata.confidenceIndicator === "number" && (
                  <ConfidenceIndicator value={metadata.confidenceIndicator} size="small" />
                )}
              </div>
            </div>
            <Text type="body2" as="p" className={faultSerieValue}>
              {t("commun.tooltip.label_value", {
                label: (
                  <Text type="body2" as="span">
                    {label}
                  </Text>
                ),
                value: (
                  <Text type="body2SemiBold" as="span">
                    {value}
                  </Text>
                ),
              })}
            </Text>
          </div>,
        );
      }

      return renderToString(
        <div className={layoutTooltips}>
          <div>
            <Text type="body1SemiBold" className={title}>
              {quantity.name}
            </Text>
            <Text type="body2" className={cx(description, lineClamp)} title={description}>
              {quantity.description}
            </Text>
          </div>
          {faults}
        </div>,
      );
    },
    [appendCSS, skipZeroImpact],
  );
}

//#region CSS
const layoutTooltips = css`
  max-width: 350px;
  min-width: 250px;
`;

// @ts-expect-error
const _global = css`
  :global() {
    .highcharts-tooltip:has(.${layoutTooltips}) span:first-child {
      min-width: max-content;
    }
  }
`;

const faultSerie = css`
  column-gap: ${spacings.xxs};
  display: grid;
  grid-template-columns: repeat(2, max-content);
  padding-top: ${spacings.xs};
  row-gap: ${spacings.xxs};
  align-items: center;
  color: ${colors.carbon800};

  &:not(:last-child) {
    padding-bottom: ${spacings.xs};
    border-bottom: 1px solid ${colors.titanium200};
  }
`;

const faultSerieInfo = css`
  display: flex;
  align-items: center;
  gap: ${spacings.m};
`;

const faultSerieName = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const faultSerieIcons = css`
  position: relative;
`;

const faultSerieValue = css`
  grid-column: 2 / span 1;
`;

const title = css`
  width: 100%;
  white-space: wrap;
`;

const square = css`
  height: 14px;
  width: 14px;
  margin-right: 5px;
  flex-shrink: 0;
`;

const description = css`
  padding-top: ${spacings.xxs};
  color: ${color.carbon700};
`;

const lineClamp = css`
  word-break: break-all;
  text-overflow: ellipsis;
  overflow: hidden;

  @supports (-webkit-line-clamp: 2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const starIcon = css`
  position: absolute;
  top: -5px;
  left: -5px;
`;

//#endregion CSS
